<template lang="pug">
.budget-form
  ValidationObserver(
    ref="budgetForm"
    slim
  )
    .toggle-multiple-options.project-options.mb-20(v-if="showMembersLimit")
      label.btn.btn-toggle-option.view-toggle(
        :class="{ active: activePill === 'budget', error: budgetTabErrors }"
        @click="activePill = 'budget'"
      )
        | Project budget
      label.btn.btn-toggle-option.view-toggle(
        :class="{ active: activePill === 'members', error: membersTabErrors }"
        @click="activePill = 'members'"
      )
        | Member limits
    ValidationObserver(
      v-slot="{ errors }"
      ref="budgetLimitsForm"
      slim
    )
      BudgetLimitsForm(
        v-if="!isBudgetProjectMode"
        :class="{ hidden: activePill !== 'budget' }"
        :allow-stop-timer="allowStopTimer"
        :organization="organization"
        :duration-format="durationFormat"
        :budget-state="budgetState"
        :has-budget="hasBudget"
        :disabled="disabled"
        :cleared-fields="clearedFields"
        :extended-reset-options="extendedResetOptions"
        :allow-notify-all-members="allowNotifyAllMembers"
        :only-batch-fields="onlyBatchFields"
        :errors="errors"
        @change-budget-field="handleChangeBudgetField"
        @clear-budget="handleClearBudget"
        @errors-changed="handleErrorsChanged(errors, 'budget')"
      )

      BudgetLimitsProjectForm(
        v-else
        :class="{ hidden: activePill !== 'budget' }"
        :allow-stop-timer="allowStopTimer"
        :organization="organization"
        :duration-format="durationFormat"
        :budget-state="budgetState"
        :has-budget="hasBudget"
        :disabled="disabled"
        :cleared-fields="clearedFields"
        :extended-reset-options="extendedResetOptions"
        :allow-notify-all-members="allowNotifyAllMembers"
        :only-batch-fields="onlyBatchFields"
        :errors="errors"
        @change-budget-field="handleChangeBudgetField"
        @clear-budget="handleClearBudget"
        @errors-changed="handleErrorsChanged(errors, 'budget')"
      )

    ValidationObserver(
      v-slot="{ errors }"
      ref="memberLimitsForm"
      slim
    )
      BudgetMembersLimitForm.mt-10(
        v-if="showMembersLimit"
        :class="{ hidden: activePill !== 'members' }"
        :allow-stop-timer="allowStopTimer"
        :organization="organization"
        :duration-format="durationFormat"
        :members-limits-state="membersLimitsState"
        :disabled="disabled"
        :extended-reset-options="extendedResetOptions"
        :member-limit-fields-required="memberLimitFieldsRequired"
        :errors="errors"
        @change-budget-field="handleChangeBudgetField"
        @change-members-limits-field="handleChangeMembersLimitsField"
        @member-added="handleMemberAdded"
        @errors-changed="handleErrorsChanged(errors, 'members')"
      )
</template>

<script>
import BudgetLimitsForm from './BudgetLimitsForm.vue';
import BudgetLimitsProjectForm from './BudgetLimitsProjectForm.vue';
import BudgetMembersLimitForm from './BudgetMembersLimitForm.vue';

export default {
  name: 'BudgetsForm',
  components: { BudgetLimitsForm, BudgetMembersLimitForm, BudgetLimitsProjectForm },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    budgetState: {
      type: Object,
      required: true,
    },
    membersLimitsState: {
      type: Object,
      default: () => ({ alert: {}, groups: [] }),
    },
    hasBudget: {
      type: Boolean,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
    durationFormat: {
      type: String,
      default: 'h:mm',
    },
    allowStopTimer: {
      type: Boolean,
      default: false,
    },
    allowNotifyAllMembers: {
      type: Boolean,
      default: false,
    },
    showMembersLimit: {
      type: Boolean,
      default: false,
    },
    extendedResetOptions: {
      type: Boolean,
      default: false,
    },
    onlyBatchFields: {
      type: Boolean,
      default: false,
    },
    selectedToggle: {
      type: String,
      default: 'budget',
      validator: (value) => {
        return ['', 'budget', 'members'].includes(value);
      },
    },
  },
  emits: ['change-budget-field', 'change-members-limits-field', 'clear-budget'],
  data() {
    return {
      clearedFields: false,
      activePill: 'budget',
      budgetTabErrors: false,
      membersTabErrors: false,
    };
  },
  computed: {
    memberLimitFieldsRequired() {
      const membersLimit = (this.membersLimitsState.groups || [{}]).filter((limit) => !limit._destroy);

      if (membersLimit.length === 0) {
        return false;
      }

      if (membersLimit.length > 1) {
        return true;
      }

      return !!(
        membersLimit[0].type ||
        membersLimit[0].rate ||
        membersLimit[0].cost ||
        membersLimit[0].start_date ||
        membersLimit[0].members.length ||
        membersLimit[0].recurrence
      );
    },
    isBudgetProjectMode() {
      return this.$store?.state?.budgetProjectMode;
    },
  },
  mounted() {
    this.activePill = this.selectedToggle ? this.selectedToggle : 'budget';
  },
  methods: {
    handleClearBudget() {
      this.clearedFields = true;
      this.$emit('clear-budget');
    },
    handleChangeBudgetField(field, val) {
      this.$emit('change-budget-field', field, val);
      this.$forceUpdate();
    },
    handleChangeMembersLimitsField(field, val) {
      this.$emit('change-members-limits-field', field, val);
      this.$forceUpdate();
    },
    handleMemberAdded() {
      this.$emit('member-added');
    },
    handleErrorsChanged(errors, type) {
      const newErrors = errors || {};

      this[`${type}TabErrors`] = Object.values(newErrors).some((value) => value.length > 0);
    },
  },
};
</script>
